import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PortalUsersList } from './PortalUsersList';
import { AddPortalUserDialog } from './AddPortalUserDialog';
import { EditPortalUserDialog } from './EditPortalUserDialog';
import { useContractorPortalUsers, ContractorPortalUser } from '@/hooks/useContractorPortalUsers';
import { UserPlus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ContractorPortalUsersSectionProps {
  companyId: string;
}

export function ContractorPortalUsersSection({ companyId }: ContractorPortalUsersSectionProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ContractorPortalUser | null>(null);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const {
    portalUsers,
    isLoading,
    createPortalUser,
    sendInvitation,
    updatePortalUser,
    deletePortalUser,
  } = useContractorPortalUsers(companyId);

  const handleCreateUser = async (data: any) => {
    await createPortalUser(data);
    setAddDialogOpen(false);
  };

  const handleEditUser = (user: ContractorPortalUser) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (id: string, data: any) => {
    await updatePortalUser({ id, data });
    setEditDialogOpen(false);
  };

  const handleResendInvitation = async (userId: string) => {
    await sendInvitation(userId);
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    await updatePortalUser({ id: userId, data: { is_active: isActive } });
  };

  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      await deletePortalUser(userToDelete);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading portal users...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Portal Users</h3>
          <p className="text-sm text-muted-foreground">
            Manage contractor access to the portal
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Portal User
        </Button>
      </div>

      <PortalUsersList
        users={portalUsers || []}
        onEdit={handleEditUser}
        onResendInvitation={handleResendInvitation}
        onToggleActive={handleToggleActive}
        onDelete={handleDeleteClick}
      />

      <AddPortalUserDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSubmit={handleCreateUser}
      />

      <EditPortalUserDialog
        user={selectedUser}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSubmit={handleUpdateUser}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Portal User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this portal user? This action cannot be undone.
              The user will lose access to the contractor portal immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
